import { Button } from "GameObjects/UI/Button";
import * as Constants from "Data/Constants";
import * as HLF from "html-living-framework";
import * as Topics from "Data/Topics";
import * as SFX from "Data/SoundEffect";
import * as VO from "Data/VoiceOver";
import Settings from "Data/Settings";
import {IntroLayout} from "Layouts/IntroLayout";
import { AbstractGameState } from "./AbstractGameState";
import { MainMenu } from "./MainMenu";
import { LayoutSprite } from "../GameObjects/UI/LayoutSprite";
import { Back, Sine, TimelineMax, TweenLite, TweenMax } from "gsap";
import { FlyingGame } from "./FlyingGame";
import GameState from "Data/GameState";
import { LayoutButton } from "../GameObjects/UI/LayoutButton";
import { Background } from "GameObjects/UI/Background";
import { AnimatedSprite } from "html-living-framework";
import { Application } from "Application";
import { LayoutNineSliceSprite } from "GameObjects/UI/LayoutNineSliceSprite";

export class Intro extends AbstractGameState<typeof IntroLayout> implements ReturnType<typeof IntroLayout> {

    public static readonly NAME = "Intro";
    public readonly layout = IntroLayout;
    public readonly skipButton: LayoutButton;
    public readonly bg: LayoutSprite;
    public readonly jackie: LayoutSprite;
    public readonly matt: LayoutSprite;
    public readonly inez: LayoutSprite;
    public readonly digit: LayoutSprite;
    public readonly duck: LayoutSprite[];
    public readonly progressBarBg: LayoutSprite;
    public readonly progressBarFill: LayoutNineSliceSprite;
    public readonly progressBarStartIcon: LayoutSprite;
    public readonly progressBarRestStopMarkerVisited: LayoutSprite;
    public readonly progressBarRestStopMarker: LayoutSprite;
    public readonly progressBarDestinationIcon: LayoutSprite;

    protected jackieY: number = 1;
    protected mattY: number = 1;
    protected inezY: number = 1;

    protected hoverTimeline?: TimelineMax;

    protected bgContainer: Background;
    protected animatedDucks: AnimatedSprite[] = [];
    protected ducksContainer: PIXI.Container;
    protected progressBarContainer: PIXI.Container;
    protected bgm = HLF.Application.instance.audio.getAudioTrack("flyingloop", HLF.AudioCategory.BGM) ?? HLF.Application.instance.audio.createAudioTrack("flyingloop", HLF.AudioCategory.BGM);

    public constructor() {
        super();
        this.bgContainer = new Background();
        this.bg = new LayoutSprite();
        this.jackie = new LayoutSprite();
        this.matt = new LayoutSprite();
        this.inez = new LayoutSprite();
        this.digit = new LayoutSprite();
        this.skipButton = new LayoutButton(()=>{
            Application.instance.analytics.event("select_content", "skip");
            this.startNewGame();
        }, VO.BUTTON_SKIP_INTRO_HOVER, SFX.UI_CLICK);
        this.duck = [
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
        ];
        this.ducksContainer = new PIXI.Container();
        this.progressBarContainer = new PIXI.Container();

        this.progressBarBg = new LayoutSprite();
        this.progressBarFill = new LayoutNineSliceSprite();
        this.progressBarStartIcon = new LayoutSprite();
        this.progressBarRestStopMarkerVisited = new LayoutSprite();
        this.progressBarRestStopMarker = new LayoutSprite();
        this.progressBarDestinationIcon = new LayoutSprite();

        this._subscriptions.push(
            PubSub.subscribe(HLF.KEYBOARD_FOCUS_BEGIN, this.onKeyboardFocusBegin.bind(this)),
        );
    }
    private get characters() {
        return [this.matt, this.jackie, this.inez, this.digit];
    }

    public init(pSize: PIXI.Point, pData: {name: string}) {
        // draw order
        this.addChildAt(this.bgContainer, 0);
        this.bgContainer.addChild(this.bg);
        this.addChild(this.skipButton);
        this.addChild(this.jackie);
        this.addChild(this.matt);
        this.addChild(this.inez);
        this.addChild(this.digit);
        this.addChild(this.progressBarContainer);
        this.progressBarContainer.addChild(this.progressBarBg);
        this.progressBarContainer.addChild(this.progressBarFill);
        this.progressBarContainer.addChild(this.progressBarStartIcon);
        this.progressBarContainer.addChild(this.progressBarRestStopMarkerVisited);
        this.progressBarContainer.addChild(this.progressBarRestStopMarker);
        this.progressBarContainer.addChild(this.progressBarDestinationIcon);
        this.addChild(this.ducksContainer);
        super.init(pSize, pData);

        this.jackieY = this.jackie.y;
        this.inezY = this.inez.y;
        this.mattY = this.matt.y;

        this.duck.forEach(placeholderDuck => {
            const animatedDuck = this.ducksContainer.addChild(new AnimatedSprite());
            animatedDuck.addAnimation("flying", new HLF.SpriteAnimation(placeholderDuck.name.replace(/\d+$/, ''), undefined as any as string, 7, 2, 12, true, 1));

            animatedDuck.position.copyFrom(placeholderDuck.position);
            animatedDuck.scale.copyFrom(placeholderDuck.scale);
            animatedDuck.play("flying");
            this.animatedDucks.push(animatedDuck);
        });

        this.homeButton.visible = false;
        this.characterContainer.visible = false;
        this.helpButton.visible = false;
        this.settingsButton.visible = false;
        this.progressBarContainer.alpha = 0;
    }

    public animateIn(pOnComplete: ()=>void) {
        super.animateIn(pOnComplete);
        this.skipButton.interactive = false; // hack to prevent keyboard from focusing immediately
        PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [
            this.skipButton,
        ]);
        this.skipButton.interactive = true;

        this.hoverTimeline = new TimelineMax();
        this.hoverTimeline.repeat(-1);
        this.hoverTimeline.staggerFromTo([...this.characters, ...this.animatedDucks], 1, {
            pixi: {
                anchorY: 1,
            },
        },
            {
                pixi: {
                    anchorY: 0.9,
                },
                ease: Sine.easeInOut,
            }, 0.25)
            .staggerFromTo([...this.characters, ...this.animatedDucks], 0.95, {
                pixi: {
                    anchorY: 0.9,
                },
            },
                {
                    pixi: {
                        anchorY: 1,
                    },
                    ease: Sine.easeInOut
                }, 0.05);

        this.hoverTimeline.paused(!Settings.moreAnimation);

        TweenMax.from(this.ducksContainer, 5, {pixi: {x: -1024 - this.duck[0].width}}).delay(15);
        TweenMax.to(this.ducksContainer, 5, {pixi: {x: 1024}}).delay(25);

        this.playTheVoiceovers();
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        this.animatedDucks.forEach(d=>d.update(pDeltaTime));
    }

    public destroy(pOptions: Parameters<typeof HLF.State.prototype.destroy>[0]) {
        super.destroy(pOptions);
        this.hoverTimeline?.kill();
        TweenMax.killTweensOf(this.ducksContainer);
    }

    protected startNewGame() {
        GameState.flyingTimeElapsed = 0;
        GameState.freePlay = false;
        this.gotoNextState(FlyingGame.NAME);
    }

    protected playHelpVO() {
        // noop
    }

    protected onSettingsChanged(pTopic: typeof Topics.SETTINGS_CHANGED, pSettings: Readonly<typeof Settings>): void {
        super.onSettingsChanged(pTopic, pSettings);
        this.hoverTimeline?.paused(!pSettings.moreAnimation);
    }

    protected onKeyboardFocusBegin(pTopic: typeof HLF.KEYBOARD_FOCUS_BEGIN, pData: HLF.Input.IFocusable): void {
        if (pData === this.skipButton) {
            Application.instance.voiceover.stopVO();
            Application.instance.voiceover.playVO(VO.BUTTON_SKIP_INTRO_HOVER, ()=>{
                this.playTheVoiceovers();
            });
        }
    }

    protected onVoiceoverStarted(pTopic: typeof HLF.VOICEOVER_STARTED, pData: string): void {
        // no need to call super.onVoiceoverStarted here
        if (pData.indexOf("jackie") === 0) {
            TweenLite.to(this.jackie, 0.5, {pixi: {y: this._size.y - 150}});
            TweenLite.to(this.matt, 0.5, {pixi: {y: this.mattY}});
            TweenLite.to(this.inez, 0.5, {pixi: {y: this.inezY}});
        }
        else if (pData.indexOf("matt") === 0) {
            TweenLite.to(this.jackie, 0.5, {pixi: {y: this.jackieY}});
            TweenLite.to(this.matt, 0.5, {pixi: {y: this._size.y - 150}});
            TweenLite.to(this.inez, 0.5, {pixi: {y: this.inezY}});
        }
        else if (pData.indexOf("inez") === 0) {
            TweenLite.to(this.jackie, 0.5, {pixi: {y: this.jackieY}});
            TweenLite.to(this.matt, 0.5, {pixi: {y: this.mattY}});
            TweenLite.to(this.inez, 0.5, {pixi: {y: this._size.y - 150}});
        }
    }

    private playTheVoiceovers() {
        const vosToPlay = [
            VO.INTRO_1,
            VO.INTRO_2,
            VO.INTRO_3,
            VO.INTRO_4,
        ];

        if (Settings.audioDescriptionEnabled) {
            vosToPlay.push(VO.AUDIO_DESCRIPTION_1);
        }

        vosToPlay.push(
            VO.INTRO_5,
            VO.INTRO_6
        );

        Application.instance.voiceover.playVO(vosToPlay, () => {
            TweenLite.to(this.progressBarContainer, 0.25, { pixi: { alpha: 1 } });
            const tweenMe = { x: 1, y: 1 };
            TweenLite.fromTo(tweenMe, 1, { x: 0 }, { x: 1 }).eventCallback(Constants.TWEEN_ONUPDATE, () => {
                this.progressBarFill.setNineSliceScale(tweenMe.x, tweenMe.y);
            });
            TweenLite.from(this.progressBarDestinationIcon, 1.5, { pixi: { scale: 0 }, ease: Back.easeOut.config(1.25) });

            const nextVosToplay = [];
            if (Settings.audioDescriptionEnabled) {
                nextVosToplay.push(VO.AUDIO_DESCRIPTION_FLYING_INTRO_2);
            }
            nextVosToplay.push(
                VO.INTRO_7,
                VO.INTRO_8,
                VO.INTRO_9
            );

            Application.instance.voiceover.playVO(nextVosToplay, () => {
                this.skipButton.visible = false;
                this.startNewGame();
            });
        });
    }
}
